import { Icon } from '@iconify/react';
import type { IconName } from 'lucide-react/dynamic';
import { dynamicIconImports } from 'lucide-react/dynamic';
import * as React from 'react';

import type { SocialPlatform } from '@/core/types/profile-modules';

/** Props accepted by social platform icons. */
export type SocialIconProps = {
  className?: string;
};

/** Icon component type (Iconify wrapper). */
export type SocialIconComponent = React.ComponentType<SocialIconProps>;

function createIconifyIcon(iconId: string): SocialIconComponent {
  const IconComponent = ({ className }: SocialIconProps) =>
    React.createElement(Icon, {
      icon: iconId,
      className,
    });
  IconComponent.displayName = `SocialIcon(${iconId})`;
  return IconComponent;
}

function safeLucideIconName(raw: string): IconName {
  const t = raw.trim();
  if (t && t in dynamicIconImports) return t as IconName;
  return 'link';
}

/** Parse stored Lucide icon name for custom links; invalid values fall back to `link`. */
export function parseCustomSocialIconName(raw?: string): IconName {
  return safeLucideIconName(raw ?? '');
}

/** Platform URL templates: {username} is replaced with the username (without @) */
const PLATFORM_URL_PATTERNS: Partial<Record<SocialPlatform, string>> = {
  instagram: 'https://instagram.com/{username}',
  linkedin: 'https://linkedin.com/in/{username}',
  twitter: 'https://x.com/{username}',
  x: 'https://x.com/{username}',
  facebook: 'https://facebook.com/{username}',
  tiktok: 'https://tiktok.com/@{username}',
  youtube: 'https://youtube.com/@{username}',
  github: 'https://github.com/{username}',
  behance: 'https://behance.net/{username}',
  dribbble: 'https://dribbble.com/{username}',
  pinterest: 'https://pinterest.com/{username}',
  snapchat: 'https://snapchat.com/add/{username}',
  twitch: 'https://twitch.tv/{username}',
};

/**
 * Visible URL prefix for handle-only inputs (e.g. `instagram.com/`).
 * Returns null for custom / unknown platforms that need a full URL.
 */
export function getSocialHandlePrefix(platform: SocialPlatform): string | null {
  const pattern = PLATFORM_URL_PATTERNS[platform];
  if (!pattern) return null;
  return pattern.replace(/^https:\/\//, '').replace('{username}', '');
}

/** Hosts whose profile URLs are recognised per platform (`www.`/`m.` allowed). */
const PLATFORM_HOSTS: Partial<Record<SocialPlatform, readonly string[]>> = {
  instagram: ['instagram.com'],
  linkedin: ['linkedin.com'],
  twitter: ['x.com', 'twitter.com'],
  x: ['x.com', 'twitter.com'],
  facebook: ['facebook.com', 'fb.com'],
  tiktok: ['tiktok.com'],
  youtube: ['youtube.com'],
  github: ['github.com'],
  behance: ['behance.net'],
  dribbble: ['dribbble.com'],
  pinterest: ['pinterest.com', 'pinterest.de'],
  snapchat: ['snapchat.com'],
  twitch: ['twitch.tv'],
};

function hostMatchesPlatform(platform: SocialPlatform, hostname: string) {
  const host = hostname.toLowerCase();
  return (PLATFORM_HOSTS[platform] ?? []).some(
    (known) => host === known || host.endsWith(`.${known}`),
  );
}

function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function handleFromPathSegments(
  platform: SocialPlatform,
  segments: string[],
): string | undefined {
  switch (platform) {
    case 'linkedin':
      return segments[0] === 'in' ? segments[1] : undefined; // /in/username
    case 'snapchat':
      return segments[0] === 'add' ? segments[1] : segments[0]; // /add/username
    case 'tiktok':
    case 'youtube':
      return segments[0]?.startsWith('@') ? segments[0] : undefined; // /@username
    default:
      return segments[0];
  }
}

/**
 * Username from a pasted profile URL of the given platform
 * (`https://www.linkedin.com/in/jane` → `jane`). Null when the input is not
 * such a URL, e.g. a plain username or another platform's link.
 */
export function parseSocialProfileUrl(
  platform: SocialPlatform,
  input: string,
): string | null {
  const value = input.trim();
  if (!value || platform === 'custom') return null;

  const candidate = /^[a-z][a-z\d+.-]*:\/\//i.test(value)
    ? value
    : /^[\w-]+(\.[\w-]+)+\//.test(value)
      ? `https://${value}`
      : null;
  if (!candidate) return null;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }
  if (!hostMatchesPlatform(platform, parsed.hostname)) return null;

  const segments = parsed.pathname.split('/').filter(Boolean).map(safeDecode);
  const handle = handleFromPathSegments(platform, segments)?.replace(/^@/, '');
  return handle || null;
}

/** Username to store for a platform input: pasted profile URL or `@handle` → `handle`. */
export function normalizeSocialHandle(
  platform: SocialPlatform,
  input: string,
): string {
  return (
    parseSocialProfileUrl(platform, input) ?? input.trim().replace(/^@/, '')
  );
}

const ASCII_HANDLE = /^[A-Za-z0-9._-]{1,100}$/;
/** LinkedIn vanity URLs may contain letters of any script. */
const LINKEDIN_HANDLE = /^[\p{L}\p{N}._-]{1,100}$/u;

/**
 * Whether a normalised username can be used in the platform URL. Rejects
 * spaces, slashes, umlauts and other characters that yield a broken link.
 * Custom links (full URLs) are always valid here.
 */
export function isValidSocialHandle(
  platform: SocialPlatform,
  handle: string,
): boolean {
  if (platform === 'custom') return true;
  return (platform === 'linkedin' ? LINKEDIN_HANDLE : ASCII_HANDLE).test(
    handle,
  );
}

/**
 * Build full URL from platform and username (or full URL for custom).
 * For "custom", value is used as-is; if it doesn't start with http, "https://" is prepended.
 * For platforms, a pasted profile URL is reduced to its username first.
 */
export function getSocialUrl(
  platform: SocialPlatform,
  usernameOrUrl: string,
): string {
  const value = (usernameOrUrl || '').trim();
  if (!value) return '';

  if (platform === 'custom') {
    return value.startsWith('http://') || value.startsWith('https://')
      ? value
      : `https://${value}`;
  }

  const pattern =
    PLATFORM_URL_PATTERNS[platform as keyof typeof PLATFORM_URL_PATTERNS];
  if (!pattern) return value;

  const username = normalizeSocialHandle(platform, value);
  return pattern.replace('{username}', encodeURIComponent(username));
}

/**
 * Extract display username from URL for a given platform.
 * For "custom", returns the full URL. A link saved before DEV-546 with a pasted
 * URL as the username comes back as that (decoded) URL, so the list shows it
 * as broken; the edit form reduces it again via `normalizeSocialHandle`.
 */
export function getSocialUsername(
  platform: SocialPlatform,
  url: string,
): string {
  const u = (url || '').trim();
  if (!u) return '';

  if (platform === 'custom') return u;

  return parseSocialProfileUrl(platform, u) ?? u;
}

/**
 * Get icon component for a social platform.
 * All icons use Simple Icons (Iconify) for a consistent filled style.
 * Use this map to avoid creating components during render.
 */
export const SOCIAL_ICONS: Record<SocialPlatform, SocialIconComponent> = {
  instagram: createIconifyIcon('simple-icons:instagram'),
  linkedin: createIconifyIcon('simple-icons:linkedin'),
  x: createIconifyIcon('simple-icons:x'),
  twitter: createIconifyIcon('simple-icons:x'),
  facebook: createIconifyIcon('simple-icons:facebook'),
  tiktok: createIconifyIcon('simple-icons:tiktok'),
  youtube: createIconifyIcon('simple-icons:youtube'),
  github: createIconifyIcon('simple-icons:github'),
  behance: createIconifyIcon('simple-icons:behance'),
  dribbble: createIconifyIcon('simple-icons:dribbble'),
  pinterest: createIconifyIcon('simple-icons:pinterest'),
  snapchat: createIconifyIcon('simple-icons:snapchat'),
  twitch: createIconifyIcon('simple-icons:twitch'),
  custom: createIconifyIcon('mdi:link'),
};

export function getSocialIcon(platform: SocialPlatform): SocialIconComponent {
  return SOCIAL_ICONS[platform] ?? SOCIAL_ICONS.custom;
}
